import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { Alert, AlertTitle, Link, Stack, Typography } from '@mui/material'
import {
  ForgotPasswordBody,
  forgotPasswordBodySchema
} from '@shared/dto/auth.dto'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FundingLogos from '../../components/common/FundingLogos'
import { TextInput } from '../../components/forms/TextInput'
import { LoaderButton } from '../../components/LoaderButton'
import { useForgotPasswordMutation } from '../../hooks/useMutations/useAuthMutations'
import { useSnackbar } from '../../hooks/useSnackbar'

export default function ForgotPassword() {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<{
    title: string
    description: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty }
  } = useForm<ForgotPasswordBody>({
    defaultValues: {
      email: ''
    },
    resolver: standardSchemaResolver(forgotPasswordBodySchema),
    mode: 'onTouched'
  })

  const forgotPasswordMutation = useForgotPasswordMutation()

  const onSubmit = (data: ForgotPasswordBody) => {
    forgotPasswordMutation.mutate(data, {
      onSuccess: () => {
        setShowSuccess(true)
      },
      onError: (error: any) => {
        if (
          error.status === 400 &&
          error.response.data.data.reason === 'InvalidParameterException'
        ) {
          setErrorMessage({
            title: t('forgot_password.email_not_verified.title'),
            description: t('forgot_password.email_not_verified.description')
          })
          return
        }

        setErrorMessage({
          title: t('forgot_password.internal_error.title'),
          description: t('forgot_password.internal_error.description')
        })
      }
    })
  }

  const onSubmitResend = (data: ForgotPasswordBody) => {
    forgotPasswordMutation.mutate(data, {
      onSuccess: () => {
        showSnackbar({
          message: t('forgot_password.resend.success'),
          severity: 'success'
        })
      },
      onError: () => {
        showSnackbar({
          message: t('forgot_password.resend.error'),
          severity: 'error'
        })
      }
    })
  }

  return (
    <>
      {showSuccess ? (
        <Stack
          direction='column'
          spacing='1.5rem'
          sx={{
            width: '37.875rem',
            minHeight: '39rem',
            padding: '2.5rem',
            borderRadius: '1.5rem',
            backgroundColor: 'background.default'
          }}
        >
          <Typography variant='h4'>
            {t('forgot_password.success.title')}
          </Typography>
          <Typography variant='h6' color='text.secondary'>
            {t('forgot_password.success.description')}
          </Typography>
          <Alert severity='success'>
            <AlertTitle>{t('forgot_password.success.alert.title')}</AlertTitle>
            {t('forgot_password.success.alert.description')}
          </Alert>
          <Typography variant='body1'>
            {t('forgot_password.success.not_received')}
          </Typography>
          <Link
            variant='body1'
            component='button'
            onClick={handleSubmit(onSubmitResend)}
            sx={{
              color: forgotPasswordMutation.isPending
                ? 'text.disabled'
                : 'primary.main',
              textAlign: 'left'
            }}
          >
            {t('forgot_password.resend.link')}
          </Link>
          <FundingLogos size='small' sx={{ mt: '1.5rem' }} />
        </Stack>
      ) : (
        <Stack
          direction='column'
          sx={{
            width: '37.875rem',
            minHeight: '39rem',
            padding: '2.5rem',
            borderRadius: '1.5rem',
            backgroundColor: 'background.default'
          }}
        >
          <Stack direction='column' spacing='1.5rem' mb='6rem'>
            <Typography variant='h4'>{t('forgot_password.title')}</Typography>
            <Typography variant='h6' color='text.secondary'>
              {t('forgot_password.description_1')}
            </Typography>
            <Typography variant='h6' color='text.secondary'>
              {t('forgot_password.description_2')}
            </Typography>
          </Stack>

          <Stack direction='column' spacing='1.5rem'>
            <TextInput
              name='email'
              register={register}
              errors={errors}
              label={t('sign_up.email')}
            />
            <LoaderButton
              onClick={handleSubmit(onSubmit)}
              disabled={
                !isValid || !isDirty || forgotPasswordMutation.isPending
              }
              loading={forgotPasswordMutation.isPending}
              startIcon={<ChevronLeftIcon />}
              label={
                forgotPasswordMutation.isPending
                  ? t('forgot_password.button_loading')
                  : t('forgot_password.button')
              }
              sx={{ textTransform: 'none', padding: '0.5rem 1.375rem' }}
            />
            <Alert
              severity='error'
              sx={{
                visibility: errorMessage ? 'visible' : 'hidden',
                color: 'rgba(95, 33, 32, 1)',
                backgroundColor: '#FDEDED'
              }}
            >
              <AlertTitle>{errorMessage?.title}</AlertTitle>
              {errorMessage?.description}
            </Alert>
          </Stack>
          <FundingLogos size='small' sx={{ mt: '1.5rem' }} />
        </Stack>
      )}
    </>
  )
}
